import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SORTS = [
    { v: "featured", l: "Featured" },
    { v: "newest", l: "Newest" },
    { v: "price_low", l: "Price: Low to High" },
    { v: "price_high", l: "Price: High to Low" },
    { v: "best_selling", l: "Best Selling" },
];

export default function Shop() {
    const [params, setParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);

    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const sort = params.get("sort") || "featured";
    const availability = params.get("availability") || "";
    const maxPrice = params.get("max_price") || "";

    const setParam = (key, val) => {
        const next = new URLSearchParams(params);
        if (val) next.set(key, val);
        else next.delete(key);
        setParams(next);
    };

    const load = useCallback(async () => {
        setLoading(true);
        const sp = new URLSearchParams();
        if (q) sp.set("q", q);
        if (category) sp.set("category", category);
        if (sort) sp.set("sort", sort);
        if (availability) sp.set("availability", availability);
        if (maxPrice) sp.set("max_price", maxPrice);
        try {
            const { data } = await api.get(`/products?${sp.toString()}`);
            setProducts(data.products);
        } finally {
            setLoading(false);
        }
    }, [q, category, sort, availability, maxPrice]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        api.get("/categories").then(({ data }) => setCats(data.categories));
    }, []);

    return (
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-10 md:py-14">
            <div className="mb-8">
                <p className="eyebrow mb-3">Shop</p>
                <h1 className="font-serif text-4xl md:text-5xl tracking-tight">{q ? `Results for “${q}”` : "The Collection"}</h1>
            </div>

            <div className="flex flex-col lg:flex-row gap-10">
                {/* Filters */}
                <aside className="lg:w-64 shrink-0">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
                        <SlidersHorizontal className="h-4 w-4" /> Filters
                    </div>
                    <div className="space-y-6 text-sm">
                        <div>
                            <p className="font-medium mb-3">Category</p>
                            <div className="space-y-2">
                                <button data-testid="filter-cat-all" onClick={() => setParam("category", "")} className={`block ${!category ? "text-gold" : "text-muted-foreground hover:text-ink"} transition-colors`}>All Products</button>
                                {cats.map((c) => (
                                    <button key={c.id} data-testid={`filter-cat-${c.slug}`} onClick={() => setParam("category", c.name)} className={`block ${category === c.name ? "text-gold" : "text-muted-foreground hover:text-ink"} transition-colors`}>{c.name}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <p className="font-medium mb-3">Max Price</p>
                            <input data-testid="filter-max-price" type="range" min="500" max="10000" step="500" value={maxPrice || 10000} onChange={(e) => setParam("max_price", e.target.value === "10000" ? "" : e.target.value)} className="w-full accent-gold" />
                            <p className="text-xs text-muted-foreground mt-1">{maxPrice ? `Up to ₹${Number(maxPrice).toLocaleString("en-IN")}` : "Any price"}</p>
                        </div>
                        <div>
                            <p className="font-medium mb-3">Availability</p>
                            <label className="flex items-center gap-2 cursor-pointer text-muted-foreground">
                                <input data-testid="filter-instock" type="checkbox" checked={availability === "in_stock"} onChange={(e) => setParam("availability", e.target.checked ? "in_stock" : "")} className="accent-gold" />
                                In stock only
                            </label>
                        </div>
                    </div>
                </aside>

                {/* Grid */}
                <div className="flex-1">
                    <div className="flex items-center justify-between mb-6">
                        <p className="text-sm text-muted-foreground">{products.length} product{products.length !== 1 ? "s" : ""}</p>
                        <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
                            <SelectTrigger data-testid="sort-select" className="w-52 bg-white border-[#E8DFD5]"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton aspect-[4/5]" />)}
                        </div>
                    ) : products.length === 0 ? (
                        <div className="py-24 text-center text-muted-foreground">
                            <p className="font-serif text-2xl text-ink mb-2">No products found</p>
                            <p>Try adjusting your filters or search.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {products.map((p) => <ProductCard key={p.id} product={p} />)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
